import { computed, inject, Injectable, Signal } from '@angular/core';
import { FeatureFlagAccessCheckerService, PermissionAccessCheckerService } from './access-checkers';
import { CapabilityAccessChecker, CapabilityAccessResult, CapabilityName, CapabilityNames } from './capability.model';

/**
 * Resolves capabilities from registered checkers. A capability is available only
 * when at least one checker grants it and none deny it.
 *
 * Note: shared-auth and shared-common cannot use this service because it depends
 * on them, creating a circular dependency. They must access FeatureFlagsService directly.
 */
@Injectable({ providedIn: 'root' })
export class CapabilityService {
  private readonly checkers: readonly CapabilityAccessChecker[] = this.getCheckers();
  private readonly capabilities: Record<CapabilityName, Signal<boolean>> = this.createCapabilitySignals();

  /** Returns a reactive availability signal for the given capability. */
  public isAvailable(name: CapabilityName): Signal<boolean> {
    return this.capabilities[name];
  }

  private getCheckers(): readonly CapabilityAccessChecker[] {
    return [
      inject(FeatureFlagAccessCheckerService),
      inject(PermissionAccessCheckerService),
    ];
  }

  private createCapabilitySignals(): Record<CapabilityName, Signal<boolean>> {
    return Object.fromEntries(
      CapabilityNames.map(name => [name, this.createAvailabilitySignal(name)]),
    ) as Record<CapabilityName, Signal<boolean>>;
  }

  private createAvailabilitySignal(name: CapabilityName): Signal<boolean> {
    return computed(() => {
      const accessResults: CapabilityAccessResult[] = this.checkers.map(checker => checker.canUse(name)());

      const isAccessGranted: boolean = accessResults.some(result => result === true);
      const isAccessDenied: boolean = accessResults.some(result => result === false);

      return isAccessGranted && !isAccessDenied;
    });
  }
}
